import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import className from 'classnames/bind';
import styles from 'feature/license-plates/index.module.scss';
import LicensePlateItem from 'feature/license-plates/license-plate-item';
import LicensePlateDialog from 'feature/license-plates/license-plate-dialog';
import CardItem from 'components/card-item';
import Icon from 'components/icon';
import PageLayout from 'layout/page';
import { useAppContext } from 'use-app';
import {
  useLicensePlateContext,
  SELECT_LICENSE_PLATE,
  SET_SERVICE_DIALOG,
} from 'feature/license-plates/use-license-plate';
import iconCircleArrowRight from 'assets/icon_circle_arrow_right.svg';
import Info from 'components/info';
import iconReport from 'assets/icon_report.svg';
import ButtonDialog from 'components/button-dialog';
import { PATH, config, isCpcpay, isNpcpay, isLineBank } from 'config';
import amplitude from 'service/amplitude';
import { getUserInfo } from 'helpers/data-check-helper';
import CrossDistributorBindingPlateDialog from './cross-distributor-binding-plate-dialog';
const cx = className.bind(styles);

const LicensePlatesLayout = ({ showMotoHint = false, children }) => {
  const { state: appState } = useAppContext();
  const { state, dispatch } = useLicensePlateContext();
  const history = useHistory();
  const location = useLocation();
  const { hasPayments, hasLicensePlates } = getUserInfo(appState);
  const licensePlatesContentClasses = cx({
    'license-plates-layout': true,
    'show-moto-hint': showMotoHint,
  });

  const serviceDialogOpen = () => {
    dispatch({
      type: SET_SERVICE_DIALOG,
      payload: true,
    });
  };
  const serviceDialogClose = () => {
    dispatch({
      type: SET_SERVICE_DIALOG,
      payload: false,
    });
  };

  const CreditCardAlter = () => {
    if (!hasPayments && hasLicensePlates) {
      const text = () => {
        if (isCpcpay || isNpcpay) return '請完成付款設定，才能使用停車付款服務';

        return '請完成付款設定，才能使用加油停車付款服務';
      };
      return (
        <Info
          onClick={() => {
            if (isCpcpay) {
              history.push(PATH.MOCHIPAY_FLOW_CONFIRM);
            } else if (isNpcpay) {
              history.push(PATH.MOCHIPAY_FLOW_SETTING);
            } else {
              history.push(PATH.MOCHIPAY_FLOW_CREDIT_CARD);
            }
          }}
          withArrow={true}
          customStyle={{ margin: '-30px -24px 20px' }}
        >
          {text()}
        </Info>
      );
    }

    return <></>;
  };

  const NeedHelp = () => {
    return (
      <div className={styles['service-hint']} onClick={serviceDialogOpen}>
        需要幫忙嗎
        <Icon imgSrc={iconReport} height="14px" alt="icon-report" />
      </div>
    );
  };

  return (
    <>
      <div className={styles.page}>
        <PageLayout>
          <CreditCardAlter />
          <div className={licensePlatesContentClasses}>
            <div className={styles['license-plates']}>{children}</div>
            {!isLineBank && <NeedHelp />}
          </div>
        </PageLayout>
      </div>
      <ButtonDialog
        isShow={state.serviceDialog}
        title="選擇需要的協助"
        items={[
          {
            content: '新增更多車牌',
            theme: 'positive',
            onClick: () => {
              serviceDialogClose();
              history.push({
                pathname: PATH.CONTACT,
                state: { from: location.pathname, issueGroupKey: 'Account', issueKey: 'Account::AddMorePlate' },
              });
            },
          },
          {
            content: '車牌已被他人綁定',
            theme: 'positive',
            onClick: () => {
              serviceDialogClose();
              history.push({
                pathname: PATH.CONTACT,
                state: { from: location.pathname, issueGroupKey: 'Account', issueKey: 'Account::PlateIsAlreadyBinded' },
              });
            },
          },
          {
            content: '其他問題',
            theme: 'positive',
            onClick: () => {
              serviceDialogClose();
              history.push({
                pathname: PATH.CONTACT,
                state: { from: location.pathname, issueGroupKey: 'Account' },
              });
            },
          },
          {
            content: '取消',
            theme: 'cancel',
            onClick: serviceDialogClose,
          },
        ]}
      />
    </>
  );
};

const JoinPlateCardItem = () => {
  const history = useHistory();

  return (
    <CardItem
      style={{ height: 70, padding: '0 24px' }}
      fontColor="blue"
      content="加入付款車牌"
      rightContent={<Icon imgSrc={iconCircleArrowRight} height="20px" alt="icon-cricle-arrow-right" />}
      onClick={() => {
        amplitude.plateNumber_add_onClick();
        return isLineBank
          ? (window.location.href = config.lineBankLicensePlatesPage)
          : history.push(PATH.LICENSE_PLATES_NEW);
      }}
    />
  );
};

const EmptyContent = () => {
  return (
    <LicensePlatesLayout>
      <JoinPlateCardItem />
    </LicensePlatesLayout>
  );
};

const LicensePlates = () => {
  const { state: appState } = useAppContext();
  const { state, dispatch } = useLicensePlateContext();
  const { maxVehicleCounts } = getUserInfo(appState);
  let licensePlates = [appState.licensePlates][0] || [];
  let allPlateTypes = licensePlates.map(plate => plate.vehicleType);
  let hasMoto = allPlateTypes.includes('moto');

  const NewLicensePlateItem = () => {
    return (
      <div className={styles['license-plates-item']}>
        <JoinPlateCardItem />
      </div>
    );
  };

  const selectPlateNumber = licensePlate => {
    dispatch({ type: SELECT_LICENSE_PLATE, payload: licensePlate });
  };

  if (licensePlates.length === 0) return <EmptyContent />;

  return (
    <>
      <LicensePlatesLayout showMotoHint={hasMoto}>
        <div className={styles['license-plates-list']}>
          {licensePlates.map(licensePlate => {
            return (
              <LicensePlateItem
                key={licensePlate.id}
                licensePlate={licensePlate}
                onClick={() => {
                  amplitude.plateNumber_plate_onClick();
                  selectPlateNumber(licensePlate);
                }}
              />
            );
          })}
          {maxVehicleCounts > licensePlates.length ? <NewLicensePlateItem /> : ''}
        </div>
        <LicensePlateDialog isShow={state.showDialog} licensePlate={state.selectedLicensePlate} />
        <CrossDistributorBindingPlateDialog />
      </LicensePlatesLayout>
    </>
  );
};

export default LicensePlates;
